import { create } from 'zustand';
import { AuthService } from '@/data/services/auth.service';
import { Config } from '@/app/constants';
import { queryClient } from '@/lib/queryClient';
import { durableStorage } from '@/lib/durableStorage';
import { offlineQueryCache } from '@/lib/offlineQueryCache';
import { identifyUser, resetAnalytics } from '@/lib/analytics';
import { setSyncUserProvider } from '@/hooks/useSyncStore';
import type { StoredUser, UserRole } from '../model';
import { ROLE_HIERARCHY } from '../model';
import type { UserProfile as CommonUserProfile } from '@/common/model/types';
import type { UserProfile as LegacyUserProfile } from '@/types';

const LOGOUT_MIRROR_WAIT_MS = 2_000;

export type LoginUserInput =
  | StoredUser
  | CommonUserProfile
  | LegacyUserProfile
  | {
      id: string;
      email: string;
      name: string | null;
      role: string;
      emailVerified: boolean;
      club?: { id: string; name: string; city: string | null } | null;
    };

interface AuthControllerState {
  user: StoredUser | null;
  login: (user: LoginUserInput) => void;
  logout: () => Promise<void>;
  hasRole: (roles: UserRole[]) => boolean;
}

function mapToStoredUser(user: LoginUserInput): StoredUser {
  const name =
    'name' in user && user.name !== undefined && user.name !== null
      ? user.name
      : null;
  const club =
    'club' in user && user.club && typeof user.club === 'object'
      ? {
          id: user.club.id,
          name: user.club.name,
          city: 'city' in user.club ? user.club.city : null,
        }
      : null;
  const emailVerified =
    'emailVerified' in user && typeof user.emailVerified === 'boolean'
      ? user.emailVerified
      : true;
  return {
    id: user.id,
    email: user.email,
    name,
    role: user.role as UserRole,
    emailVerified,
    club,
  };
}

// club_id: feature flags target a club by id (VOL-132), the backend evaluates the same names.
function identifyStoredUser(user: StoredUser): void {
  identifyUser(
    user.id,
    { role: user.role, club: user.club?.name ?? null, club_id: user.club?.id ?? null },
    user.club,
  );
}

function parseStoredUser(): StoredUser | null {
  try {
    const stored = localStorage.getItem(Config.storage.keys.user);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    
    if (!parsed.id || !parsed.email || !parsed.role) {
      localStorage.removeItem(Config.storage.keys.user);
      return null;
    }
    
    return {
      id: parsed.id,
      email: parsed.email,
      name: parsed.name ?? null,
      role: parsed.role as UserRole,
      emailVerified: typeof parsed.emailVerified === 'boolean' ? parsed.emailVerified : true,
      club: parsed.club ?? null,
    };
  } catch {
    localStorage.removeItem(Config.storage.keys.user);
    return null;
  }
}

export const useAuthController = create<AuthControllerState>((set, get) => ({
  user: parseStoredUser(),

  login: (user: LoginUserInput) => {
    queryClient.clear();
    const safeUser = mapToStoredUser(user);
    localStorage.setItem(Config.storage.keys.user, JSON.stringify(safeUser));
    identifyStoredUser(safeUser);
    set({ user: safeUser });
  },

  logout: async () => {
    try {
      await AuthService.logout();
    } catch {
      // Silently handle logout failures
    }
    queryClient.clear();
    localStorage.removeItem(Config.storage.keys.user);
    // Not awaited: with the user gone, nothing is saved or restored for it anyway.
    void offlineQueryCache?.clear();
    // Also drops the IndexedDB copy: left there, the next start-up would restore
    // it. Awaited so that closing the app right after signing out cannot cut it,
    // but briefly: a stuck IndexedDB must not hold the sign-out.
    durableStorage.removeItem(Config.storage.keys.gameState);
    await durableStorage.whenMirrored(LOGOUT_MIRROR_WAIT_MS);
    resetAnalytics();
    set({ user: null });
  },

  hasRole: (roles: UserRole[]) => {
    const user = get().user;
    if (!user) return false;
    
    const userLevel = ROLE_HIERARCHY[user.role] || 0;
    const minRequiredLevel = Math.min(...roles.map(r => ROLE_HIERARCHY[r] || 0));
    
    return userLevel >= minRequiredLevel;
  },
}));

/**
 * The signed-in user (role, club) is cached until the next login: a server-side
 * change (Club plan → club admin with a club, VOL-319) stays invisible otherwise.
 * Returns true when the stored user was refreshed.
 */
export function syncStoredUser(profile: LoginUserInput): boolean {
  const { user } = useAuthController.getState();
  if (!user || user.id !== profile.id) return false;
  const fresh = mapToStoredUser(profile);
  if (fresh.role === user.role && (fresh.club?.id ?? null) === (user.club?.id ?? null)) return false;
  // Same account, unlike login(): refetch in the background rather than clear
  // the cache, so open screens do not flash back to their loading state.
  localStorage.setItem(Config.storage.keys.user, JSON.stringify(fresh));
  identifyStoredUser(fresh);
  useAuthController.setState({ user: fresh });
  void queryClient.invalidateQueries();
  return true;
}

// A session restored from storage never goes through login(): register its identity so
// analytics can attach it if consent is given later in this page load.
const restoredUser = useAuthController.getState().user;
if (restoredUser) identifyStoredUser(restoredUser);

// Queued live actions are sent only by the user who entered them (VOL-273).
setSyncUserProvider(
  () => useAuthController.getState().user?.id ?? null,
  // The name only, never the email: it is kept on a tablet other accounts use (VOL-208).
  () => useAuthController.getState().user?.name ?? null,
  // Roles the server lets send live actions: a coach sends those another account left (VOL-198).
  () => useAuthController.getState().hasRole(['COACH']),
);
